import type { Dispatch, SetStateAction } from "react"
import type { CategoryDefinition, LocalConsentState } from "./banner"
import { cn } from "./ui/cn"
import { Switch } from "./ui/switch"

interface CategoryRowProps {
  category: CategoryDefinition
  localState: LocalConsentState
  setLocalState: Dispatch<SetStateAction<LocalConsentState>>
  isExpanded: boolean
  onToggleExpanded: () => void
}

export function CategoryRow({
  category,
  localState,
  setLocalState,
  isExpanded,
  onToggleExpanded,
}: Readonly<CategoryRowProps>) {
  const { name, label, description, required } = category
  const checked = required ? true : (localState[name] ?? false)
  const id = `consent-toggle-${name}`
  const labelId = `consent-label-${name}`
  const descriptionId = `consent-description-${name}`
  const statusLabel = getConsentStatusLabel(required, checked)

  return (
    <div className="border-t border-border first:border-t-0">
      <div className="flex items-start justify-between gap-4 py-4">
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={isExpanded}
          aria-controls={descriptionId}
          className="flex flex-1 items-start gap-2 rounded-md text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={cn(
              "-ml-6 mt-0.5 shrink-0 text-muted-foreground transition-transform duration-200",
              isExpanded && "rotate-180",
            )}
            aria-hidden
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
          <span className="flex flex-col gap-0.5">
            <span id={labelId} className="text-sm font-medium text-foreground">
              {label}
            </span>
            <span
              className={cn(
                "text-xs font-normal",
                checked ? "text-foreground/70" : "text-muted-foreground",
              )}
            >
              {statusLabel}
            </span>
          </span>
        </button>
        <Switch
          id={id}
          checked={checked}
          disabled={required}
          aria-labelledby={labelId}
          aria-describedby={isExpanded ? descriptionId : undefined}
          onCheckedChange={value =>
            setLocalState(prev => ({
              ...prev,
              [name]: value,
            }))
          }
        />
      </div>
      {isExpanded ? (
        <p id={descriptionId} className="pb-4 text-sm leading-relaxed text-muted-foreground">
          {description}
        </p>
      ) : null}
    </div>
  )
}

function getConsentStatusLabel(required: boolean | undefined, checked: boolean) {
  if (required) return "Always Active"
  return checked ? "Active" : "Inactive"
}
